import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { clientApi } from '../api/client'
import { userApi } from '../api/user'
import { urlUtils, validationUtils } from '../utils'
import { authStorage } from '../utils/authStorage'
import { ClientErrorView } from '../components/ClientErrorView'
import type { Client, ClientLoadErrorType, LoginData } from '../types'

type LoginApiError = 'account_not_found' | 'invalid_credentials' | 'server_error'

const getLoginApiError = (error: unknown): LoginApiError => {
  const status = (error as { response?: { status?: number } })?.response?.status
  if (status === 406) return 'account_not_found'
  if (status && status >= 500) return 'server_error'
  if (status && status >= 400) return 'invalid_credentials'
  return 'server_error'
}

export const LoginView: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const clientId = searchParams.get('client_id')
  
  const [client, setClient] = useState<Client | null>(null)
  const [formData, setFormData] = useState<LoginData>({
    email: '',
    password: ''
  })
  const [errors, setErrors] = useState<Partial<LoginData>>({})
  const [loginApiError, setLoginApiError] = useState<LoginApiError | null>(null)
  const [clientLoadError, setClientLoadError] = useState<ClientLoadErrorType | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingClient, setIsLoadingClient] = useState(true)
  const [stayConnected, setStayConnected] = useState(false)

  // Charger le client et vérifier une session existante
  useEffect(() => {
    const loadClientAndSession = async () => {
      if (!clientId) {
        setClientLoadError('missing_client_id')
        setIsLoadingClient(false)
        return
      }

      try {
        const clientData = await clientApi.getClientByPublicId(clientId)
        setClient(clientData)

        const switchAccount = searchParams.get('switch_account') === '1'
        const token = authStorage.getToken()

        if (!switchAccount && token) {
          try {
            await userApi.getMe()
            navigate(`/account?client_id=${clientId}`, { replace: true })
            return
          } catch (error) {
            const status = (error as { response?: { status?: number } })?.response?.status
            if (status === 403 || status === 401) {
              authStorage.clearToken()
            }
          }
        }
      } catch (error) {
        console.error('Error loading client:', error)
        setClientLoadError('client_not_found')
      } finally {
        setIsLoadingClient(false)
      }
    }

    loadClientAndSession()
  }, [clientId, navigate, searchParams])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    
    if (errors[name as keyof LoginData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }))
    }
    if (loginApiError) {
      setLoginApiError(null)
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Partial<LoginData> = {}

    if (!formData.email) {
      newErrors.email = 'L\'email est requis'
    } else if (!validationUtils.isValidEmail(formData.email)) {
      newErrors.email = 'Format d\'email invalide'
    }

    if (!formData.password) {
      newErrors.password = 'Le mot de passe est requis'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm() || !clientId) return

    setLoginApiError(null)
    setIsLoading(true)
    try {
      const result = await userApi.login(formData, clientId)

      if (result.httpStatus === 302 || result.error) {
        navigate(`/otp?client_id=${clientId}`, {
          state: {
            email: formData.email,
            password: formData.password,
            stayConnected,
          },
        })
      } else if (result.token && client) {
        authStorage.persistTokenIfRequested(result.token, stayConnected)
        const redirectUrl = urlUtils.buildRedirectUrl(client.redirectUrl, {
          token: result.token,
          user_id: result.id?.toString() || '',
          email: result.email || ''
        })
        window.location.href = redirectUrl
      }
    } catch (error) {
      console.error('Login error:', error)
      setLoginApiError(getLoginApiError(error))
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoadingClient) {
    return (
      <div className="card fade-in">
        <div className="card-header">
          <div className="spinner"></div>
          <p className="card-loading-text">Chargement...</p>
        </div>
      </div>
    )
  }

  if (clientLoadError) {
    return <ClientErrorView type={clientLoadError} clientId={clientId} />
  }

  if (!client) {
    return null
  }

  return (
    <div className="card fade-in">
      <div className="card-header">
        {client.logo && (
          <img 
            src={client.logo} 
            alt={client.title} 
            style={{ height: '60px', marginBottom: '1rem' }}
          />
        )}
        <h1 className="card-title">Connexion</h1>
        <p className="card-subtitle">Connectez-vous à {client.title} avec votre compte Olecio</p>
      </div>

      {loginApiError && (
        <div
          className={`login-error-banner login-error-banner--${loginApiError}`}
          role="alert"
          aria-live="polite"
        >
          {loginApiError === 'account_not_found' ? (
            <p>
              Aucun compte n'a été trouvé avec l'adresse{' '}
              <strong>{formData.email}</strong>.{' '}
              <a href={`/signup?client_id=${clientId}`} className="link">
                Créez un compte
              </a>{' '}
              pour continuer.
            </p>
          ) : loginApiError === 'invalid_credentials' ? (
            <p>Identifiants invalides</p>
          ) : (
            <p>Une erreur est survenue, veuillez réessayer plus tard</p>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="email" className="form-label">
            Adresse email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            className={`form-input ${errors.email ? 'shake' : ''}`}
            value={formData.email}
            onChange={handleInputChange}
            placeholder="votre@email.com"
            disabled={isLoading}
            required
          />
          {errors.email && (
            <div className="form-error">{errors.email}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="password" className="form-label">
            Mot de passe
          </label>
          <input
            type="password"
            id="password"
            name="password"
            className={`form-input ${errors.password ? 'shake' : ''}`}
            value={formData.password}
            onChange={handleInputChange}
            placeholder="Votre mot de passe"
            disabled={isLoading}
            required
          />
          {errors.password && (
            <div className="form-error">{errors.password}</div>
          )}
          <p className="form-helper-text">
            <a href={`/forgot-password?client_id=${clientId}`} className="link">
              Mot de passe oublié ?
            </a>
          </p>
        </div>

        <div className="form-group form-group--checkbox">
          <label className="form-checkbox-label" htmlFor="stayConnected">
            <input
              type="checkbox"
              id="stayConnected"
              name="stayConnected"
              className="form-checkbox"
              checked={stayConnected}
              onChange={(e) => setStayConnected(e.target.checked)}
              disabled={isLoading}
            />
            Rester connecté ?
          </label>
        </div>

        <button
          type="submit"
          className="btn btn-primary btn-full"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <div className="spinner"></div>
              Connexion...
            </>
          ) : (
            'Se connecter'
          )}
        </button>
      </form>

      <div className="card-footer">
        <p className="card-footer-text">
          Pas encore de compte partagé Olecio ?{' '}
          <a 
            href={`/signup?client_id=${clientId}`}
            className="link"
          >
            Créer un compte
          </a>
        </p>
      </div>
    </div>
  )
}
